import { mergeRefs } from '@solid-primitives/refs';
import { JSX, createEffect, createMemo, createSignal, splitProps } from 'solid-js';
import { createWritableMemo } from '@solid-primitives/memo';
import Decimal from 'decimal.js-light';

import { cx } from '../../utils';
import { ClosableProvider } from '../Closable';

function Input(props: InputProps) {
  const [, others] = splitProps(props, ['onChange']);
  return (
    <ClosableProvider
      can={others.value !== ''}
      do={() => {
        (props.onChange as any)?.('');
      }}
    >
      <InputBase {...(others as any)} onInput={props.onChange && ((e) => props.onChange!(e.target.value))} />
    </ClosableProvider>
  );
}

function InputBase(props: JSX.InputHTMLAttributes<HTMLInputElement> & { prefix?: JSX.Element; suffix?: JSX.Element }) {
  const [, others] = splitProps(props, ['class', 'children', 'prefix', 'suffix', 'title']);
  const [focus, setFocus] = createSignal(false);
  const hasTitle = 'title' in props;
  const [debounceFocus, setDebounceFocus] = createSignal(false);
  createEffect<number | undefined>((pre) => {
    if (pre !== undefined) {
      clearTimeout(pre);
    }
    if (focus()) {
      setDebounceFocus(true);
    } else {
      return setTimeout(() => setDebounceFocus(false), 100) as any;
    }
  });

  const isFocus = createMemo(() => utils.false2undefined(debounceFocus()));
  return (
    <label
      class={cx('input', props.class)}
      aria-expanded={Boolean(others.value) || isFocus()}
      data-focus={isFocus()}
      data-disabled={utils.false2undefined(props.disabled)}
    >
      {hasTitle && <div class="input-label">{props.title}</div>}
      <div class="input-content">
        {props.prefix}
        <input
          {...others}
          onFocus={(e) => {
            setFocus(true);
            //@ts-expect-error 忽略事件细节
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocus(false);
            //@ts-expect-error 忽略事件细节
            props.onBlur?.(e);
          }}
        />
        {props.suffix}
      </div>
      {props.children}
    </label>
  );
}

interface NoInputProps extends Omit<
  JSX.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'onchange' | 'onInput' | 'oninput' | 'prefix' | 'title'
> {
  title?: JSX.Element;
  prefix?: JSX.Element;
  suffix?: JSX.Element;
}
export interface InputProps extends NoInputProps {
  value: string;
  onChange?(v: string): void;
}

type NumberInputProps = NoInputProps & {
  value?: number;
  onInput?: (value: string) => void;
  onChange?(num: number): void;
  /** 格式化number，可始终保留多位小数 */
  // format?(num: number): string;
  /** 是否保留末尾的零，需要配合 precision 使用 */
  preserveTrailingZeros?: boolean;
} & ValidateOptions;

type ValidationReason = 'empty' | 'nan' | 'min' | 'max' | 'precision';

interface ValidationResult {
  valid: boolean;
  value: string | null;
  reason?: ValidationReason;
  clampedValue?: string;
}

interface ValidateOptions {
  min?: number;
  max?: number;
  precision?: number;
  preserveTrailingZeros?: boolean;
}

const validateNumber = (
  value: string | number | null | undefined,
  { min, max, precision, preserveTrailingZeros }: ValidateOptions
): ValidationResult => {
  if (value === null || value === undefined || value === '') {
    return { valid: false, value: null, reason: 'empty' };
  }

  try {
    const decimal = new Decimal(String(value).replace(',', '.'));
    // 如果需要保留末尾零且指定了精度，使用 toFixed(precision)，否则使用 toFixed()
    const num = preserveTrailingZeros && precision !== undefined ? decimal.toFixed(precision) : decimal.toFixed();

    // 检查范围
    if (min !== undefined && decimal.lessThan(min)) {
      const clampedValue = preserveTrailingZeros && precision !== undefined ? min.toFixed(precision) : min.toFixed();
      return { valid: false, value: num, reason: 'min', clampedValue };
    }

    if (max !== undefined && decimal.greaterThan(max)) {
      const clampedValue = preserveTrailingZeros && precision !== undefined ? max.toFixed(precision) : max.toFixed();
      return { valid: false, value: num, reason: 'max', clampedValue };
    }

    // 检查精度
    if (precision !== undefined) {
      const rounded = decimal.todp(precision, Decimal.ROUND_DOWN);

      if (!rounded.equals(decimal)) {
        const clampedValue = preserveTrailingZeros ? rounded.toFixed(precision) : rounded.toFixed();
        return { valid: false, value: num, reason: 'precision', clampedValue };
      }
    }

    return { valid: true, value: num };
  } catch (error) {
    console.log('🚀 ~ validateNumber ~ error:', error);
    return { valid: false, value: null, reason: 'nan' };
  }
};
const formatWithValidation = (rawValue: number | undefined | string, validateOptions: ValidateOptions) => {
  const validation = validateNumber(rawValue, validateOptions);
  const nextValue = validation.valid ? validation.value! : validation.clampedValue || '';
  return [nextValue, validation] as const;
};
/**
 * precision 保留精度，精度后的小数一律舍弃
 * @param props
 * @returns
 */
function NumberInput(props: NumberInputProps) {
  let el!: HTMLInputElement;
  const [, others] = splitProps(props, [
    'onChange',
    'onInput',
    'precision',
    'max',
    'min',
    'value',
    'ref',
    'preserveTrailingZeros',
  ]);

  const [isComposing, setIsComposing] = createSignal(false);

  /**
   * 整个InpuNumber的状态流转和状态机请看README.md
   */
  const [displayValue, setDisplayValue] = createWritableMemo((prev) => {
    /**
     * 如果用户正在输入/构造一个值，这时候外部props.value变化，有两种处理：
     * 1. 忽略外部变化（以用户输入为准）, props.value延迟到用户完成输入/退出编辑态（也就是onBlur）
     * 2. 对外部变化校验并覆盖（外部优先）, 这时候props.value会立即覆盖输入框，导致输入被覆盖、光标跳动；整体体验很差
     *
     * 这里我们选择1，忽略外部变化，先以用户输入为准，直到用户完成输入/退出编辑态（也就是onBlur），这时候再对外部变化校验并覆盖
     *
     **/
    if (isComposing()) return prev;
    return formatWithValidation(props.value, {
      min: props.min,
      max: props.max,
      precision: props.precision,
      preserveTrailingZeros: props.preserveTrailingZeros,
    })[0];
  });

  /**
   *
   *
   * @param str
   * @returns
   *
   * 允许中间态的合法数字:
   *  "-"
      "1"
      "-1"
      "."
      "1."
      "1.2"
      "-.1"
      "-."

      不允许:
      "1-2"
      "-1-"
      "--1"
      "1..2"
      ".1.2"
   */
  function filterDraft(str: string): string {
    // 先过滤掉数字、-、. 以外的字符
    str = str.replace(/[^\d.]/g, '');

    // 处理 '-'：只能在开头位置
    if (str.startsWith('.')) {
      str = '0' + str;
    }

    // 处理 '.'：只能出现一次
    const parts = str.split('.');
    if (parts.length > 2) {
      // 重新拼接：第一部分 + 第一个点 + 后面所有部分的数字组合
      str = parts[0] + '.' + parts.slice(1).join('');
    }

    return str;
  }

  // Input的时候，拿到的用户在构造的潜在合法的值，这时候应该不做任何处理，不然会导致glitch，和输入被覆盖+光标跳动
  const handleInput: JSX.InputEventHandler<HTMLInputElement, InputEvent> = (e) => {
    const rawValue = e.currentTarget.value;
    let filteredValue = filterDraft(rawValue);

    if (rawValue !== filteredValue) {
      e.target.value = filteredValue;
    }

    setIsComposing(true);

    setDisplayValue(filteredValue);

    // input代表用户正在构造一个潜在合法的值，是非终态,我们直接传递rawValue
    props.onInput?.(filteredValue);
  };

  // onChange的时候，拿到的永远是用户的终值，所以需要校验和格式化，并且回传外部props.onChange
  const handleChange: JSX.ChangeEventHandler<HTMLInputElement, Event> = (e) => {
    const rawValue = e.currentTarget.value;
    setIsComposing(false);
    const nextValue = formatWithValidation(rawValue, {
      min: props.min,
      max: props.max,
      precision: props.precision,
      preserveTrailingZeros: props.preserveTrailingZeros,
    })[0];
    setDisplayValue(nextValue);
    props.onChange?.(Number(nextValue));
  };

  return (
    <InputBase
      ref={mergeRefs((dom) => (el = dom), props.ref)}
      {...(others as any)}
      value={displayValue()}
      onChange={handleChange}
      onInput={handleInput}
      inputmode="decimal"
    />
  );
}

function AmountInput(props: NumberInputProps & { currency?: string }) {
  return (
    <NumberInput {...props} prefix={<div class="input-currency">{props.currency || '$'}</div>}>
      {props.children}
    </NumberInput>
  );
}

export default Object.assign(Input, {
  Number: NumberInput,
  Amount: AmountInput,
});
